import { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { EditOutlined, OpenInNew, ReceiptOutlined } from "@mui/icons-material";

import MobileCard from "@/components/mobile/MobileCard";
import ActionMenu from "@/components/mobile/ActionMenu";
import StatusBadge from "./StatusBadge";
import { getRowActions } from "./InvoiceTable";

import { getEffectiveStatus } from "../types/common";
import { getStatusLabel } from "../constants/lifecycleStatus";
import { V2_COLORS } from "../constants/colors";
import { convertToINR, convertDate } from "@/helpers/Utils";
import { usePermissions } from "@/context/PermissionsContext";

import type { V2InvoiceListItem } from "../types/invoice";

interface MobileInvoiceCardProps {
  invoice: V2InvoiceListItem;
  isInternal: boolean;
  onClick: (invoice: V2InvoiceListItem) => void;
  onReview: (invoice: V2InvoiceListItem) => void;
  onEdit: (invoice: V2InvoiceListItem) => void;
  onCreditNote: (invoice: V2InvoiceListItem) => void;
}

export default function MobileInvoiceCard({
  invoice,
  isInternal,
  onClick,
  onReview,
  onEdit,
  onCreditNote,
}: MobileInvoiceCardProps) {
  const { hasPermission } = usePermissions();

  const actions = useMemo(() => {
    const items: { label: string; icon?: React.ReactNode; onClick: () => void; destructive?: boolean }[] = [];

    items.push({
      label: "Review Invoice",
      icon: <OpenInNew sx={{ fontSize: 18 }} />,
      onClick: () => onReview(invoice),
    });

    // Same eligibility rule as the desktop table's actions column.
    if (getRowActions(invoice, hasPermission).edit) {
      items.push({
        label: "Edit",
        icon: <EditOutlined sx={{ fontSize: 18 }} />,
        onClick: () => onEdit(invoice),
      });
    }

    if (invoice.credit_notes?.[0]) {
      items.push({
        label: "Credit Note",
        icon: <ReceiptOutlined sx={{ fontSize: 18 }} />,
        onClick: () => onCreditNote(invoice),
      });
    }

    return items;
  }, [invoice, hasPermission, onReview, onEdit, onCreditNote]);

  const effectiveStatus = getEffectiveStatus(invoice);
  const statusLabel = invoice.invoice_status?.label ?? getStatusLabel(effectiveStatus, isInternal, invoice.po_source);

  return (
    <MobileCard onClick={() => onClick(invoice)}>
      {/* Row 1: Invoice no · PO code + ActionMenu */}
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography
          sx={{ fontSize: "14px", fontWeight: 600, lineHeight: "20px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}
        >
          {invoice.invoice_no}
          <Typography component="span" sx={{ fontSize: "14px", fontWeight: 400, color: V2_COLORS.textSecondary }}>
            {" · "}
            {invoice.po_code}
          </Typography>
        </Typography>
        <Box sx={{ flexShrink: 0, ml: 0.5 }} onClick={(e) => e.stopPropagation()}>
          <ActionMenu actions={actions} />
        </Box>
      </Box>

      {/* Row 2: Invoice value + StatusBadge */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mt={0.25}>
        <Typography sx={{ fontSize: "14px", fontWeight: 700, lineHeight: "20px" }}>
          {convertToINR(invoice.invoice_value)}
        </Typography>
        <StatusBadge status={effectiveStatus} displayLabel={statusLabel} />
      </Box>

      {/* Row 3: Date · Vendor name (internal only) */}
      <Box display="flex" alignItems="center" mt={0.25}>
        <Typography sx={{ fontSize: "12px", lineHeight: "16px", color: V2_COLORS.textSecondary }}>
          {convertDate(invoice.invoice_date)}
          {isInternal && invoice.vendor_name && ` · ${invoice.vendor_name}`}
        </Typography>
      </Box>
    </MobileCard>
  );
}
